import React, { useEffect, useState } from 'react';
import { ArrowLeftRight, Plus } from 'lucide-react';
import { Card, Btn, Select, Table, Spinner, ErrBox, Modal, Input, Field } from '../../../components/UI';
import { inventoryAPI } from '../../../utils/api';
import { fmtDate } from '../../../utils/format';

export default function MovementsTab() {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState({ item_id: '', location_id: '', movement_type: '' });
  const [locations, setLocations] = useState([]);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showModal, setShowModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ item_id: '', warehouse_id: '', warehouse_to_id: '', movement_type: 'IN', quantity: 0, notes: '', reference_no: '', movement_date: new Date().toISOString().split('T')[0] });
  const [currentItem, setCurrentItem] = useState({ item_id: '', warehouse_id: '', warehouse_to_id: '', movement_type: 'IN', quantity: 0, notes: '' });

  useEffect(() => { loadRefs(); loadMovements(); }, []);

  const loadRefs = async () => {
    try {
      const [loc, itm] = await Promise.all([
        inventoryAPI.getLocations(),
        inventoryAPI.getItems('?limit=100'),
      ]);
      setLocations((loc.data || []).map(x => ({ value: x.id, label: x.nama })));
      setItems((itm.data || []).map(x => ({ value: x.id, label: x.nama })));
    } catch {}
  };

  const loadMovements = async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (filters.item_id) params.append('item_id', filters.item_id);
      if (filters.location_id) params.append('location_id', filters.location_id);
      if (filters.movement_type) params.append('movement_type', filters.movement_type);
      params.append('page', page);
      params.append('limit', 20);
      const r = await inventoryAPI.getMovements(filters);
      setMovements(r.data || []);
      setTotal(r.total || 0);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  };

  const getTypeBadge = (type) => {
    const cfg = { IN: { bg: '#ECFDF5', color: '#047857', text: 'IN' }, OUT: { bg: '#FEF2F2', color: '#991B1B', text: 'OUT' }, TRANSFER: { bg: '#DBEAFE', color: '#1E40AF', text: 'TRANSFER' }, ADJUSTMENT: { bg: '#E9D5FF', color: '#581C87', text: 'ADJUST' } };
    const c = cfg[type] || { bg: '#F3F4F6', color: '#374151', text: type };
    return <span style={{ padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: c.bg, color: c.color }}>{c.text}</span>;
  };

  const handleSave = async () => {
    if (!form.item_id || !form.warehouse_id || !form.movement_type || !form.quantity) return alert('Item, gudang, tipe, dan qty wajib diisi');
    setSaving(true);
    try {
      await inventoryAPI.createMovement(form);
      closeModal();
      loadMovements();
    } catch (e) { alert('Gagal menyimpan: ' + e.message); }
    finally { setSaving(false); }
  };

  const closeModal = () => {
    setShowModal(false);
    setForm({ item_id: '', warehouse_id: '', warehouse_to_id: '', movement_type: 'IN', quantity: 0, notes: '', reference_no: '', movement_date: new Date().toISOString().split('T')[0] });
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'flex-end' }}>
        <div style={{ minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Filter Barang</label>
          <Select value={filters.item_id} onChange={e => setFilters({ ...filters, item_id: e.target.value })} options={[{ value: '', label: 'Semua' }, ...items]} />
        </div>
        <div style={{ minWidth: 200 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Filter Lokasi</label>
          <Select value={filters.location_id} onChange={e => setFilters({ ...filters, location_id: e.target.value })} options={[{ value: '', label: 'Semua' }, ...locations]} />
        </div>
        <div style={{ minWidth: 160 }}>
          <label style={{ display: 'block', fontSize: 11, fontWeight: 600, color: '#6B7280', marginBottom: 4 }}>Tipe</label>
          <Select value={filters.movement_type} onChange={e => setFilters({ ...filters, movement_type: e.target.value })} options={[{ value: '', label: 'Semua' }, { value: 'IN', label: 'IN' }, { value: 'OUT', label: 'OUT' }, { value: 'TRANSFER', label: 'TRANSFER' }, { value: 'ADJUSTMENT', label: 'ADJUST' }]} />
        </div>
        <Btn onClick={loadMovements}>Cari</Btn>
        <Btn variant="ghost" onClick={() => setFilters({ item_id: '', location_id: '', movement_type: '' })}>Reset</Btn>
        <div style={{ marginLeft: 'auto' }}>
          <Btn icon={<Plus size={14}/>} onClick={() => setShowModal(true)}>Mutasi Baru</Btn>
        </div>
      </div>

      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#111827' }}>
            <ArrowLeftRight size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
            Riwayat Mutasi ({total})
          </h3>
        </div>

        {error && <div style={{ marginBottom: 12 }}><ErrBox msg={error} onRetry={loadMovements} /></div>}
        {loading ? <Spinner /> : movements.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9CA3AF' }}>
            <ArrowLeftRight size={40} style={{ marginBottom: 12, opacity: 0.4 }} />
            <p style={{ margin: 0 }}>Tidak ada data mutasi</p>
          </div>
        ) : (
          <>
            <Table pageSize={10} cols={[
              { key: 'movement_date', label: 'Tanggal', render: v => fmtDate(v) },
              { key: 'item_kode', label: 'Kode', nowrap: true },
              { key: 'item_nama', label: 'Barang' },
              { key: 'movement_type', label: 'Tipe', render: v => getTypeBadge(v) },
              { key: 'from_warehouse_nama', label: 'Dari', render: v => v || '-' },
              { key: 'to_warehouse_nama', label: 'Ke', render: v => v || '-' },
              { key: 'quantity', label: 'Qty', align: 'right' },
              { key: 'reference_no', label: 'Referensi', render: v => v || '-' },
              { key: 'notes', label: 'Catatan', render: v => v || '-' },
            ]} data={movements} emptyMsg="Tidak ada data mutasi" />
            {total > 20 && (
              <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginTop: 16 }}>
                <Btn size="sm" variant="ghost" disabled={page <= 1} onClick={() => { setPage(p => p - 1); loadMovements(); }}>Prev</Btn>
                <span style={{ padding: '6px 12px', fontSize: 13 }}>Halaman {page}</span>
                <Btn size="sm" variant="ghost" disabled={page * 20 >= total} onClick={() => { setPage(p => p + 1); loadMovements(); }}>Next</Btn>
              </div>
            )}
          </>
        )}
      </Card>

      {showModal && (
        <Modal title="Mutasi Stok Baru" onClose={closeModal}>
          <Field label="Tanggal">
            <Input type="date" value={form.movement_date} onChange={e => setForm({ ...form, movement_date: e.target.value })} />
          </Field>
          <Field label="Barang" required>
            <Select value={form.item_id} onChange={e => setForm({ ...form, item_id: e.target.value })} options={items} placeholder="Pilih barang..." />
          </Field>
          <Field label="Tipe" required>
            <select value={form.movement_type} onChange={e => setForm({ ...form, movement_type: e.target.value })} style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #E5E7EB', borderRadius: 8, fontSize: 13, outline: 'none', boxSizing: 'border-box' }}>
              <option value="IN">IN (Penerimaan)</option>
              <option value="OUT">OUT (Pengeluaran)</option>
              <option value="TRANSFER">TRANSFER (Transfer)</option>
              <option value="ADJUSTMENT">ADJUSTMENT (Penyesuaian)</option>
            </select>
          </Field>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
            <Field label="Dari Gudang" required>
              <Select value={form.warehouse_id} onChange={e => setForm({ ...form, warehouse_id: e.target.value })} options={locations} placeholder="Pilih gudang..." />
            </Field>
            {form.movement_type === 'TRANSFER' && (
              <Field label="Ke Gudang" required>
                <Select value={form.warehouse_to_id} onChange={e => setForm({ ...form, warehouse_to_id: e.target.value })} options={locations.filter(l => l.value !== form.warehouse_id)} placeholder="Pilih gudang tujuan..." />
              </Field>
            )}
            {form.movement_type !== 'TRANSFER' && <div />}
          </div>
          <Field label="Qty" required>
            <Input type="number" value={form.quantity || ''} onChange={e => setForm({ ...form, quantity: parseFloat(e.target.value) || 0 })} placeholder="0" />
          </Field>
          <Field label="No. Referensi">
            <Input value={form.reference_no} onChange={e => setForm({ ...form, reference_no: e.target.value })} placeholder="No. referensi (opsional)" />
          </Field>
          <Field label="Catatan">
            <Input value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Catatan (opsional)" />
          </Field>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 20 }}>
            <Btn variant="secondary" onClick={closeModal}>Batal</Btn>
            <Btn onClick={handleSave} disabled={saving}>{saving ? 'Menyimpan...' : 'Simpan'}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}